<template>
  <div class="floating-file-notes">
    <!-- Notes drawer. Was a 420px popover pinned under the header with a separate
         "fullscreen" centred modal behind an expand button -- three surfaces for one job.
         It is one drawer now: it flies in from the right edge, full height, wide enough to
         actually write in.

         Teleported to <body>: this component lives inside .header, and an overlay that
         renders inside the thing it is supposed to cover is at the mercy of every ancestor
         stacking context between here and the root. Out at body level its z-index means
         what it says. Scoped styles still apply -- Vue stamps the scope id on teleported
         nodes too. -->
    <teleport to="body">
    <transition name="drawer-slide">
      <div
        v-if="showNotesPanel"
        class="floating-file-notes__panel"
        role="dialog"
        aria-label="Notes"
        @click.stop
      >
        <div class="panel__header">
          <!-- File Notes and Sales Notes side by side as tabs, so both are always in view. -->
          <div class="panel__tabs" role="tablist" aria-label="Note type">
            <button
              type="button"
              role="tab"
              class="panel__tab"
              :class="{ 'is-active': selectedNoteType === 'file' }"
              :aria-selected="selectedNoteType === 'file' ? 'true' : 'false'"
              @click="selectNoteType('file')"
            >
              <span class="k-icon k-i-comment"></span>
              File Notes
              <span v-if="fileNoteCount > 0" class="panel__tab-count">{{ fileNoteCount }}</span>
            </button>
            <button
              type="button"
              role="tab"
              class="panel__tab"
              :class="{ 'is-active': selectedNoteType === 'sales' }"
              :aria-selected="selectedNoteType === 'sales' ? 'true' : 'false'"
              @click="selectNoteType('sales')"
            >
              <span class="k-icon k-i-file-txt"></span>
              Sales Notes
            </button>
          </div>

          <div class="panel__header-actions">
            <button
              class="panel__close-btn"
              @click="toggleNotesPanel"
              title="Close"
            >
              <span class="k-icon k-i-x"></span>
            </button>
          </div>
        </div>
        <div class="panel__body">
          <loan-notes-component
            v-if="selectedNoteType === 'file'"
            :loan-id="loanId"
            :show-loan-notes="showNotesPanel"
          ></loan-notes-component>
          <sales-notes-component
            v-if="selectedNoteType === 'sales'"
            :loan-id="loanId"
            :show-sales-notes="showNotesPanel"
          ></sales-notes-component>
        </div>
      </div>
    </transition>
    </teleport>

    <!-- Floating Button -->
    <button
      class="floating-file-notes__button"
      @click="toggleNotesPanel"
      :title="buttonTitle"
    >
      <span class="k-icon" :class="selectedNoteType === 'file' ? 'k-i-comment' : 'k-i-file-txt'"></span>
      <span class="floating-file-notes__text">{{ currentTitle }}</span>
      <!-- File note count, so whoever picks up the file (UW, processing) can see at a glance
           whether it has notes. Hidden when there are none or the count couldn't be read. -->
      <span
        v-if="fileNoteCount > 0"
        class="floating-file-notes__count"
        :aria-label="fileNoteCount + (fileNoteCount === 1 ? ' file note' : ' file notes')"
      >{{ fileNoteCount > 99 ? '99+' : fileNoteCount }}</span>
    </button>

    <!-- Click outside overlay -->
    <teleport to="body">
      <div
        v-if="showNotesPanel || showDropdown"
        class="floating-file-notes__overlay"
        @click="closeAll"
      ></div>
    </teleport>

    <!-- The fullscreen loan-notes-dialog / sales-notes-dialog are gone: the drawer is the
         large surface now, so there is nothing left for them to expand into. Both components
         are still in the repo and still used by their own pages. -->
  </div>
</template>

<script>
import loanNotesComponent from './loanNotesComponent.vue';
import salesNotesComponent from './salesNotesComponent.vue';
import auth from '../authConfig.js';
import apiService from '../api/api.service.js';

export default {
  name: 'FloatingFileNotesButton',
  components: {
    'loan-notes-component': loanNotesComponent,
    'sales-notes-component': salesNotesComponent,
  },
  data() {
    return {
      showNotesPanel: false,
      showDropdown: false,
      selectedNoteType: 'file', // 'file' or 'sales'
      fileNoteCount: 0,
      countRequestId: 0
    };
  },
  watch: {
    // Every loan the header shows gets its own count.
    loanId: { immediate: true, handler: 'loadFileNoteCount' },
    // Re-count when the drawer closes, so a note just added shows up in the badge.
    showNotesPanel(open) {
      if (!open) this.loadFileNoteCount();
    }
  },
  computed: {
    loanId() {
      return this.$route.query.id;
    },
    currentTitle() {
      return this.selectedNoteType === 'file' ? 'File Notes' : 'Sales Notes';
    },
    buttonTitle() {
      const n = this.fileNoteCount;
      const count = n > 0 ? ` (${n} file note${n === 1 ? '' : 's'} on this loan)` : ' (no file notes yet)';
      return 'Open ' + this.currentTitle + count;
    }
  },
  methods: {
    toggleNotesPanel() {
      this.showNotesPanel = !this.showNotesPanel;
      if (!this.showNotesPanel) {
        this.showDropdown = false;
      }
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    selectNoteType(type) {
      this.selectedNoteType = type;
      this.showDropdown = false;
    },
    closeAll() {
      this.showNotesPanel = false;
      this.showDropdown = false;
    },
    // Same endpoint the File Notes drawer uses, just counted. A failure only hides the badge;
    // the drawer reports its own errors when opened.
    async loadFileNoteCount() {
      const loanId = this.loanId;
      const requestId = ++this.countRequestId;
      if (!loanId) {
        this.fileNoteCount = 0;
        return;
      }
      try {
        const tokenResponse = await auth.acquireTokenResponse();
        const res = await apiService.GetLoanNotes(loanId, tokenResponse.accessToken);
        // Ignore a slow answer for a loan we've already moved off of.
        if (requestId !== this.countRequestId) return;
        const notes = res?.data?.data?.notes;
        this.fileNoteCount = Array.isArray(notes) ? notes.length : 0;
      } catch (err) {
        if (requestId !== this.countRequestId) return;
        console.warn('[FileNotes] could not load note count', err);
        this.fileNoteCount = 0;
      }
    }
  }
};
</script>

<style scoped>
/* Lives in the header's action row now, so the root is just an inline anchor. */
/* No z-index here. `position: relative` plus a z-index creates a stacking context, and
   this element sits inside .header -- so the drawer and its scrim, both children, were
   confined to z-999 no matter what they asked for. Position alone creates no context. */
.floating-file-notes {
  position: relative;
  display: inline-flex;
  align-items: center;
}

/* Overlay for click-outside-to-close */
/* A real scrim now: the drawer is a full-height overlay, so the page behind it should
   recede. It was transparent when this was a small popover. */
.floating-file-notes__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(14, 34, 68, .38);
}

/* Notes Panel */
/* Full-height drawer pinned to the right edge. Wide enough to write in, capped so it
   never swallows the whole screen on a large monitor, and narrowing to the full width on
   a phone. */
.floating-file-notes__panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(620px, 92vw);
  background: var(--gs-card, #fff);
  border-left: 1px solid var(--gs-border, #e7ebf3);
  border-radius: 0;
  box-shadow: -24px 0 60px rgba(21, 48, 90, .22);
  display: flex;
  flex-direction: column;
  z-index: 1001;
  overflow: hidden;
}

.panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  color: var(--gs-ink, #16305a);
  border-bottom: 1px solid var(--gs-border-soft, #eef1f7);
}

.panel__title-container {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.panel__title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gs-ink, #16305a);
}

.panel__tabs { display: flex; gap: 4px; align-items: flex-end; align-self: stretch; }
.panel__tab {
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border: 0; border-bottom: 3px solid transparent;
  background: none; font: inherit; font-size: 1.5rem; font-weight: 600; color: var(--gs-muted, #8a95a8); cursor: pointer;
}
.panel__tab:hover { color: var(--gs-ink, #16305a); }
.panel__tab.is-active { color: var(--gs-ink, #16305a); border-bottom-color: var(--gs-blue, #2f6fed); }
.panel__tab-count {
  min-width: 18px; padding: 0 6px; border-radius: 999px; background: var(--gs-blue-soft, #e9f0ff); color: var(--gs-ink, #16305a);
  font-size: 1.1rem; font-weight: 700; line-height: 18px; text-align: center;
}
.dropdown-container {
  position: relative;
}

.dropdown-toggle {
  padding: 4px 6px !important;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: #fff;
  border: 1px solid var(--gs-border, #e7ebf3);
  border-radius: var(--gs-radius-sm, 8px);
  box-shadow: 0 10px 26px rgba(17, 40, 80, 0.14);
  min-width: 160px;
  overflow: hidden;
  z-index: 1001;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: #ffffff !important;
  border: none;
  color: var(--gs-ink, #16305a) !important;
  font-size: 1.25rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.dropdown-item:hover {
  background: var(--gs-blue-soft, #e9f0ff) !important;
  color: var(--gs-blue, #2f6fed) !important;
}

.dropdown-item .k-icon {
  font-size: 16px;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.panel__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel__action-btn,
.panel__close-btn {
  background: transparent;
  border: none;
  color: var(--gs-muted, #8a95a8);
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s ease, color 0.2s ease;
}

  .panel__action-btn:hover,
  .panel__close-btn:hover {
    background: #f2f5fa;
    color: var(--gs-ink, #16305a);
  }

.panel__action-btn .k-icon,
.panel__close-btn .k-icon {
  font-size: 18px;
}

/* Was a fixed 500px, which was the whole point of the old popover being cramped. The
   drawer is full height, so the body just takes what is left under the header. */
.panel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Flies in from the right edge rather than popping open under the header. */
.drawer-slide-enter-active {
  transition: transform .22s cubic-bezier(.2, .7, .2, 1);
}

.drawer-slide-leave-active {
  transition: transform .18s ease-in;
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}

.panel-expand-enter-active {
  animation: panel-expand-in 0.3s ease-out;
}

.panel-expand-leave-active {
  animation: panel-expand-out 0.2s ease-in;
}

@keyframes panel-expand-in {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
    transform-origin: top right;
  }
  to {
    opacity: 1;
    transform: none;
    transform-origin: top right;
  }
}

@keyframes panel-expand-out {
  from {
    opacity: 1;
    transform: none;
    transform-origin: top right;
  }
  to {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
    transform-origin: top right;
  }
}

/* Header trigger — same shape/feel as the bell next to it */
.floating-file-notes__button {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  background: transparent;
  color: var(--gs-ink, #16305a);
  border: none;
  border-radius: 8px;
  font-size: 1.25rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-shadow: none;
  transition: background 0.15s ease;
  position: relative;
  white-space: nowrap;
}

.floating-file-notes__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--gs-navy, #15305a);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.floating-file-notes__button:hover {
  background: #f5f8fd;
}

.floating-file-notes__button .k-icon {
  font-size: 1.7rem;
  color: var(--gs-ink-soft, #46566f);
}

.floating-file-notes__text {
  white-space: nowrap;
}

/* the label collapses on smaller screens — the icon carries it */
@media (max-width: 1200px) {
  .floating-file-notes__text {
    display: none;
  }
}
</style>

<style>
/* Non-scoped styles to override loan-notes-component within the panel */
.floating-file-notes__panel .loan-notes-component {
  height: 100% !important;
  max-height: 100% !important;
}

.floating-file-notes__panel .loan-notes-component .notes__container {
  height: 300px !important;
  max-height: 300px !important;
  overflow-y: scroll !important;
  overflow-x: hidden !important;
  -webkit-overflow-scrolling: touch;
}

.floating-file-notes__panel .loan-notes-component .notes__container::-webkit-scrollbar {
  width: 6px;
  display: block !important;
}

.floating-file-notes__panel .loan-notes-component .notes__container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.floating-file-notes__panel .loan-notes-component .notes__container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.floating-file-notes__panel .loan-notes-component .notes__container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.floating-file-notes__panel .loan-notes-component .add-note__container {
  position: relative !important;
}
</style>
